import { parseGame } from "@mliebelt/pgn-parser";
import type { PgnMove } from "@mliebelt/pgn-types";

export type MaskedCustomNags = {
  sources: string[];
  replacements: ReadonlyMap<string, string>;
};

type PgnSourceSpan = {
  text: string;
  maskable: boolean;
};

const numericNagPattern = /\$\d+\b/g;
const customSlashNagPattern = /\$\d+\/[^\s(){}]+/g;
const firstPlaceholder = 900000;

export function maskCustomSlashNags(
  sources: readonly string[],
): MaskedCustomNags {
  const used = new Set<string>();
  for (const source of sources) {
    for (const match of source.matchAll(numericNagPattern)) used.add(match[0]);
  }
  const replacements = new Map<string, string>();
  let next = firstPlaceholder;
  const maskedSources = sources.map((source) =>
    source.replace(customSlashNagPattern, (customNag) => {
      let placeholder = `$${next}`;
      while (used.has(placeholder)) {
        next += 1;
        placeholder = `$${next}`;
      }
      used.add(placeholder);
      replacements.set(placeholder, customNag);
      next += 1;
      return placeholder;
    }),
  );
  return { sources: maskedSources, replacements };
}

export function restoreCustomSlashNags(
  source: string,
  replacements: ReadonlyMap<string, string>,
): string {
  return source.replace(numericNagPattern, (nag) => replacements.get(nag) ?? nag);
}

export function findProtectedSpanEnd(source: string, start: number): number {
  const delimiter = source[start];

  if (delimiter === "{") {
    const closingIndex = source.indexOf("}", start + 1);
    return closingIndex < 0 ? source.length : closingIndex + 1;
  }

  if (delimiter === ";") {
    let end = start + 1;
    while (end < source.length && source[end] !== "\r" && source[end] !== "\n") {
      end += 1;
    }
    return end;
  }

  if (delimiter === '"') {
    let end = start + 1;
    while (end < source.length) {
      const character = source[end];
      if (character === "\\") {
        const escaped = source[end + 1];
        if (escaped === "\\" || escaped === '"') {
          end += 2;
          continue;
        }
      }
      if (character === '"') return end + 1;
      end += 1;
    }
    return source.length;
  }

  return start + 1;
}

function splitPgnSourceIntoSpans(source: string): PgnSourceSpan[] {
  const spans: PgnSourceSpan[] = [];
  let spanStart = 0;
  let index = 0;

  while (index < source.length) {
    const character = source[index];
    if (character !== "{" && character !== ";" && character !== '"') {
      index += 1;
      continue;
    }

    if (spanStart < index) {
      spans.push({ text: source.slice(spanStart, index), maskable: true });
    }

    const protectedEnd = findProtectedSpanEnd(source, index);
    spans.push({
      text: source.slice(index, protectedEnd),
      maskable: false,
    });
    index = protectedEnd;
    spanStart = protectedEnd;
  }

  if (spanStart < source.length) {
    spans.push({ text: source.slice(spanStart), maskable: true });
  }

  return spans;
}

export function maskPgnMovetextCustomSlashNags(
  sources: readonly string[],
): MaskedCustomNags {
  const spanGroups = sources.map(splitPgnSourceIntoSpans);
  const allSpans = spanGroups.flat();
  const masked = maskCustomSlashNags(allSpans.map((span) => span.text));
  let spanIndex = 0;

  return {
    sources: spanGroups.map((spans) =>
      spans
        .map((span) => {
          const maskedText = masked.sources[spanIndex] ?? span.text;
          spanIndex += 1;
          return span.maskable ? maskedText : span.text;
        })
        .join(""),
    ),
    replacements: masked.replacements,
  };
}

function restoreParsedMoveNags(
  moves: PgnMove[] | undefined,
  replacements: ReadonlyMap<string, string>,
): void {
  const pending = [{ moves: moves ?? [], index: 0 }];
  while (pending.length > 0) {
    const frame = pending[pending.length - 1]!;
    if (frame.index >= frame.moves.length) {
      pending.pop();
      continue;
    }
    const move = frame.moves[frame.index++]!;
    if (move.nag) {
      move.nag = move.nag.map((nag) =>
        restoreCustomSlashNags(nag, replacements),
      );
    }
    const variations = move.variations ?? [];
    for (let index = variations.length - 1; index >= 0; index -= 1) {
      pending.push({ moves: variations[index]!, index: 0 });
    }
  }
}

export function parseGamePreservingCustomSlashNags(
  source: string,
): ReturnType<typeof parseGame> {
  const masked = maskPgnMovetextCustomSlashNags([source]);
  const parsed = parseGame(masked.sources[0] ?? source);
  restoreParsedMoveNags(parsed?.moves, masked.replacements);
  return parsed;
}
